/**
 * 背景ノイズ（.Bg__noise）をランダムに揺らすアニメーション。
 * 参考: anyba/src/script/module/bgnoise.js（random-figure-animation）を addict 用に移植。
 * 各要素ごとに FRAME_COUNT フレーム分のランダムな transform（translate ＋ scale反転）を事前生成し、
 * rAF で経過時間 → フレーム番号に変換して適用。DURATION 経過でループ再開＝延々ランダムに動く。
 * scale に負値（反転）を混ぜてノイズパターンの反復を隠し、フィルムグレイン風の生きた質感を出す。
 * prefers-reduced-motion では動かさない（静止）。
 */

/** 事前生成するランダムフレーム数 */
const FRAME_COUNT = 240
/** 1ループの長さ（ms）。240フレームをこの時間で1周する（≒125ms/フレーム） */
const DURATION = 30000
/** translate のランダム範囲（%） */
const TRANSLATE_MIN = -10
const TRANSLATE_MAX = 10
/** scale に使う値（負値＝上下左右の反転でパターン反復を隠す） */
const SCALE_VALUES = [1.2, -1.2]

/** 1フレーム分の transform 値 */
type NoiseFrame = {
  translateX: number
  translateY: number
  scaleX: number
  scaleY: number
}

/**
 * min〜max のランダムな数値を返す
 * @param {number} min - 最小値
 * @param {number} max - 最大値
 * @returns {number} ランダムな数値
 */
const randomNumber = (min: number, max: number): number => Math.random() * (max - min) + min

/**
 * 配列からランダムな要素を返す
 * @param {number[]} items - 選択対象の配列
 * @returns {number} ランダムに選ばれた要素
 */
const randomItem = (items: number[]): number => items[Math.floor(Math.random() * items.length)]

/**
 * FRAME_COUNT 分のランダムな transform フレームを生成する
 * @returns {NoiseFrame[]} フレーム配列
 */
const generateFrames = (): NoiseFrame[] =>
  Array.from({ length: FRAME_COUNT }, () => ({
    translateX: randomNumber(TRANSLATE_MIN, TRANSLATE_MAX),
    translateY: randomNumber(TRANSLATE_MIN, TRANSLATE_MAX),
    scaleX: randomItem(SCALE_VALUES),
    scaleY: randomItem(SCALE_VALUES),
  }))

/**
 * フレーム値を要素の transform に適用する
 * @param {HTMLElement} el - 対象要素
 * @param {NoiseFrame} f - 適用するフレーム
 */
const applyFrame = (el: HTMLElement, f: NoiseFrame): void => {
  el.style.transform = `translate(${f.translateX}%, ${f.translateY}%) scale(${f.scaleX}, ${f.scaleY})`
}

/**
 * 初期化。.Bg__noise を rAF でランダムに揺らす。
 * prefers-reduced-motion では動かさない（CSSの静止 scale のまま）。
 */
const initBgNoise = (): void => {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

  const targets = Array.from(document.querySelectorAll<HTMLElement>(".Bg__noise"))
  if (targets.length === 0) return

  // 要素ごとに独立したランダムフレーム列を持たせる（複数 .Bg があっても揺れがズレる）
  const frameSets = targets.map(() => generateFrames())

  // SP は約12fps に間引く（フィルムグレインなので見た目ほぼ不変・全画面fixed画像の毎フレーム合成コストを削減）。
  // PC は 0（毎フレーム）で現状維持。
  const mql = window.matchMedia("(max-width: 1024px)")
  const minInterval = (): number => (mql.matches ? 80 : 0)

  let startTime: number | null = null
  let lastApplied = 0

  /**
   * rAF コールバック：経過時間からフレーム番号を出し、全要素に適用（DURATION で剰余ループ）。
   * SP は minInterval で間引き、タブ非表示中は書き込みを止める。
   * @param {number} timestamp - rAF のタイムスタンプ
   */
  const tick = (timestamp: number): void => {
    if (startTime === null) startTime = timestamp
    if (!document.hidden && timestamp - lastApplied >= minInterval()) {
      lastApplied = timestamp
      const progress = ((timestamp - startTime) % DURATION) / DURATION
      const frameIndex = Math.floor(progress * FRAME_COUNT) % FRAME_COUNT
      targets.forEach((el, i) => applyFrame(el, frameSets[i][frameIndex]))
    }
    requestAnimationFrame(tick)
  }

  requestAnimationFrame(tick)
}

initBgNoise()
